import { isValidEmail } from '@brazilian-utils/brazilian-utils'
import { CreateUserPayload } from '@User/Data/DataSource/API/Entity/User'
import { UserContainer, UserRegister } from '@User/Data/DataSource/Container'
import { CreateUser } from '@User/Domain/UseCases'
import { ChangeEvent, useCallback, useState } from 'react'
import { toast } from 'react-toastify'

interface CreateUserState {
    name: string
    mail: string
    /** Authorized password exception (third parties, service accounts). */
    passwordException: boolean
    exceptionReason: string
    password: string
}

const INITIAL_STATE: CreateUserState = {
    name: '',
    mail: '',
    passwordException: false,
    exceptionReason: '',
    password: '',
}

export const useCreateUser = () => {
    const createUserUseCase = UserContainer.get<CreateUser>(
        UserRegister.UseCaseCreateUser
    )
    const [state, setState] = useState<CreateUserState>(INITIAL_STATE)
    const [loading, setLoading] = useState(false)

    const handleChange = ({ target }: ChangeEvent<HTMLInputElement>) => {
        const { name, value, type, checked } = target
        setState((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }))
    }

    const handleCreateUser = useCallback(
        (onClose?: () => void | Promise<void>) => {
            const handleSubmit = async () => {
                if (!state.name.trim()) {
                    toast.error('Nome do usuário é obrigatório')
                    return
                }

                if (!state.mail.trim()) {
                    toast.error('E-mail do usuário é obrigatório')
                    return
                }

                if (!isValidEmail(state.mail)) {
                    toast.error('E-mail inválido')
                    return
                }

                if (state.passwordException) {
                    if (!state.exceptionReason.trim()) {
                        toast.error('Informe o motivo da exceção de login com senha')
                        return
                    }
                    if (state.password.length < 6) {
                        toast.error('Senha deve ter pelo menos 6 caracteres')
                        return
                    }
                }

                setLoading(true)
                try {
                    await createUserUseCase.execute({
                        name: state.name.trim(),
                        mail: state.mail.trim(),
                        roles: [],
                        ...(state.passwordException
                            ? {
                                  passwordException: {
                                      reason: state.exceptionReason.trim(),
                                      password: state.password,
                                  },
                              }
                            : {}),
                    })
                    toast.success('Usuário cadastrado com sucesso')
                    await onClose?.()
                    setState(INITIAL_STATE)
                } catch (error) {
                    console.error(error)
                    toast.error('Erro ao cadastrar usuário')
                }
                setLoading(false)
            }

            return {
                state,
                handleChange,
                handleSubmit,
                loading,
            }
        },
        [createUserUseCase, state]
    )

    return {
        state,
        handleChange,
        handleCreateUser,
        loading,
    }
}